import React from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@material-ui/core';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { GET_TASK_STAGES } from '../../../tasks/TasksList/task-stages.gql';
import { GET_ONBOARDING_OPTION_STAGES } from '../../../../../common/onboarding-option-task/onboarding-option-stages-model.gql';
import { GetTaskStages } from '../../../tasks/TasksList/__generated__/GetTaskStages';
import { GetOnboardingOptionStages } from '../../../../../common/onboarding-option-task/__generated__/GetOnboardingOptionStages';

interface OnboardingStageAssignmentProps {
  optionType: string;
  optionLabel: string;
  enabled: boolean;
}

export const OnboardingStageAssignment: React.FC<OnboardingStageAssignmentProps> = ({
  optionType,
  optionLabel,
  enabled,
}) => {
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const assignmentMutation = useCampfireFetch({ defer: true });

  // Fetch available task stages
  const { data: taskStagesData } = useCampfireQuery<GetTaskStages, {}>(GET_TASK_STAGES);

  // Fetch current onboarding option stage assignments
  const { data: onboardingStagesData, refetch: refetchOnboardingStages } = useCampfireQuery<
    GetOnboardingOptionStages,
    {}
  >(GET_ONBOARDING_OPTION_STAGES);

  const taskStages = taskStagesData?.vm.taskStages || [];

  // Find the current assignment for this option type
  const currentAssignment = onboardingStagesData?.vm.onboardingOptionStages.find(
    (stage) => stage.optionType === optionType
  );

  const currentStageId = currentAssignment?.taskStage?.taskStageId || '';

  const handleStageChange = async (newStageId: string) => {
    try {
      const endpoint = newStageId
        ? `${apiUrl}/vm/admin-console/onboarding-option-stage/assign`
        : `${apiUrl}/vm/admin-console/onboarding-option-stage/unassign`;

      const data = newStageId ? { optionType, taskStageId: newStageId } : { optionType };

      const response = await assignmentMutation.run({
        url: endpoint,
        method: 'post',
        data,
      });

      if (response.ok) {
        setSnackbar({
          variant: 'success',
          open: true,
          message: `${optionLabel} stage assignment updated successfully`,
        });

        // Refetch to update the UI
        refetchOnboardingStages();
      } else {
        throw new Error('Failed to update stage assignment');
      }
    } catch (error) {
      setSnackbar({
        variant: 'error',
        open: true,
        message: `Failed to update ${optionLabel} stage assignment`,
      });
    }
  };

  if (!enabled) {
    return null;
  }

  return (
    <Box style={{ marginTop: '20px', marginLeft: '16px' }}>
      <Typography
        variant='subtitle2'
        style={{
          fontSize: '14px',
          color: '#666',
          marginBottom: '12px',
          fontWeight: 600,
        }}
      >
        Stage Selection
      </Typography>
      <FormControl variant='outlined' size='small' style={{ minWidth: '200px' }}>
        <InputLabel id={`stage-select-${optionType}`}>Assign to Stage</InputLabel>
        <Select
          labelId={`stage-select-${optionType}`}
          value={currentStageId}
          onChange={(e) => handleStageChange(e.target.value as string)}
          label='Assign to Stage'
          disabled={assignmentMutation.isLoading}
        >
          <MenuItem value=''>
            <Typography variant='body2' style={{ fontStyle: 'italic' }}>
              No Stage (Additional Section)
            </Typography>
          </MenuItem>
          {taskStages
            .sort((a, b) => a.order - b.order)
            .map((stage) => (
              <MenuItem key={stage.taskStageId} value={stage.taskStageId}>
                <Typography variant='body2'>
                  Stage {stage.order}: {stage.name}
                </Typography>
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <Typography style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
        When assigned to a stage, this option only appears once the applicant reaches that stage
      </Typography>
    </Box>
  );
};
